import { DecimalPipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AiSalesRecommendationDetailRecord, SalesAiApiService } from './sales-ai-api.service';
import { SalesAiOutputViewComponent } from './sales-ai-output-view.component';

@Component({
  standalone: true,
  selector: 'ain-sales-ai-recommendation-detail',
  imports: [
    DecimalPipe, AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule,
    InputTextModule, TextareaModule, TableModule, AinDatePipe, SalesAiOutputViewComponent
  ],
  template: `
    <ain-page [title]="t('sales.ai.recommendations.detailTitle')" [description]="t('sales.ai.recommendations.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else if (detail()) {
        <header class="card summary">
          <a class="back-link" [routerLink]="['/sales/ai-recommendations']">{{ t('catalog.action.backToList') }}</a>
          <article><span>{{ t('sales.ai.fields.recommendationType') }}</span><strong>{{ detail()!.recommendation.recommendationType }}</strong></article>
          <article><span>{{ t('sales.ai.fields.score') }}</span><strong>{{ (detail()!.recommendation.score * 100) | number:'1.0-1' }}%</strong></article>
          <article><span>{{ t('columns.status') }}</span><strong>{{ detail()!.recommendation.status }}</strong></article>
          <article><span>{{ t('sales.ai.fields.modelVersion') }}</span><strong>{{ detail()!.recommendation.modelVersion }}</strong></article>
        </header>
        <nav class="tabs">
          <button type="button" [class.active]="tab()==='explanation'" (click)="tab.set('explanation')">{{ t('sales.ai.tabs.explanation') }}</button>
          <button type="button" [class.active]="tab()==='override'" (click)="tab.set('override')">{{ t('sales.ai.tabs.override') }}</button>
          <button type="button" [class.active]="tab()==='feedback'" (click)="tab.set('feedback')">{{ t('sales.ai.tabs.feedback') }}</button>
          <button type="button" [class.active]="tab()==='conversation'" (click)="tab.set('conversation')">{{ t('sales.ai.tabs.conversation') }}</button>
          <button type="button" [class.active]="tab()==='forecast'" (click)="tab.set('forecast')">{{ t('sales.ai.tabs.forecast') }}</button>
        </nav>
        @if (tab() === 'explanation') {
          <section class="card">
            <p class="explanation-summary">{{ detail()!.recommendation.explanation?.summary || t('sales.ai.copilot.noExplanation') }}</p>
            <ain-sales-ai-output-view
              [titleKey]="'sales.ai.output.resultTitle'"
              [outputJson]="detail()!.recommendation.outputJson"
              [inputSnapshotJson]="detail()!.recommendation.inputSnapshotJson" />
          </section>
        }
        @if (tab() === 'override') {
          <section class="card">
            <p-table [value]="detail()!.overrides">
              <ng-template pTemplate="header">
                <tr><th>{{ t('sales.ai.fields.decision') }}</th><th>{{ t('sales.ai.fields.reason') }}</th><th>{{ t('sales.orders.fields.createdAt') }}</th></tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr><td>{{ row.decision }}</td><td>{{ row.reason }}</td><td>{{ row.createdAtUtc | ainDate:'datetime' }}</td></tr>
              </ng-template>
            </p-table>
            <div class="override-form">
              <label>{{ t('sales.ai.fields.reason') }}</label>
              <textarea pTextarea rows="3" [(ngModel)]="overrideReason"></textarea>
              <button pButton type="button" [label]="t('sales.ai.override.reject')" severity="danger" [disabled]="!overrideReason.trim()" (click)="submitOverride('Reject')"></button>
              <button pButton type="button" [label]="t('sales.ai.override.accept')" [disabled]="!overrideReason.trim()" (click)="submitOverride('Accept')"></button>
            </div>
          </section>
        }
        @if (tab() === 'feedback') {
          <section class="card">
            <p-table [value]="detail()!.feedback">
              <ng-template pTemplate="header">
                <tr><th>{{ t('sales.ai.fields.outcome') }}</th><th>{{ t('sales.ai.fields.score') }}</th><th>{{ t('sales.orders.fields.createdAt') }}</th></tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr><td>{{ row.outcome }}</td><td>{{ row.score ?? '-' }}</td><td>{{ row.createdAtUtc | ainDate:'datetime' }}</td></tr>
              </ng-template>
            </p-table>
            <div class="override-form">
              <input pInputText [(ngModel)]="feedbackOutcome" [placeholder]="t('sales.ai.fields.outcome')" />
              <button pButton type="button" [label]="t('sales.ai.feedback.submit')" [disabled]="!feedbackOutcome.trim()" (click)="submitFeedback()"></button>
            </div>
          </section>
        }
        @if (tab() === 'conversation') {
          <section class="card">
            @for (turn of detail()!.conversations; track turn.id) {
              <article class="turn"><strong>{{ turn.role }}</strong><p>{{ turn.content }}</p></article>
            } @empty { <p>{{ t('sales.ai.conversation.empty') }}</p> }
            <div class="override-form">
              <textarea pTextarea rows="3" [(ngModel)]="conversationText"></textarea>
              <button pButton type="button" [label]="t('sales.ai.conversation.add')" [disabled]="!conversationText.trim()" (click)="addConversation()"></button>
            </div>
          </section>
        }
        @if (tab() === 'forecast') {
          <section class="card">
            <button pButton type="button" [label]="t('sales.ai.forecast.run')" (click)="runForecast()"></button>
            @if (forecastOutput()) {
              <ain-sales-ai-output-view [titleKey]="'sales.ai.output.forecastTitle'" [outputJson]="forecastOutput()" />
            }
          </section>
        }
      }
    </ain-page>
  `,
  styles: [`
    .card{border:1px solid #e5e7eb;border-radius:12px;padding:12px;margin-bottom:10px;background:#fff}
    .summary{display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:10px}
    .summary article{display:grid;gap:4px}.summary span{font-size:12px;color:#6b7280}
    .back-link{color:#1d4ed8;font-weight:600;text-decoration:none;grid-column:1/-1}
    .tabs{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px}
    .tabs button{border:1px solid #d1d5db;background:#fff;border-radius:8px;padding:6px 10px;cursor:pointer}
    .tabs button.active{background:#1d4ed8;color:#fff;border-color:#1d4ed8}
    .explanation-summary{margin:0 0 12px;font-size:14px}
    .override-form{display:grid;gap:8px;margin-top:10px;max-width:480px}
    .turn{border-bottom:1px solid #e5e7eb;padding:8px 0}
    .turn p{margin:4px 0 0}
  `]
})
export class SalesAiRecommendationDetailComponent implements OnInit {
  private readonly api = inject(SalesAiApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly detail = signal<AiSalesRecommendationDetailRecord | null>(null);
  readonly tab = signal<'explanation' | 'override' | 'feedback' | 'conversation' | 'forecast'>('explanation');
  readonly forecastOutput = signal('');

  overrideReason = '';
  feedbackOutcome = '';
  conversationText = '';

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const id = params.get('recommendationId');
      if (id) this.load(id);
    });
  }

  load(id: string): void {
    this.loading.set(true);
    this.api.getRecommendation(id).subscribe({
      next: d => { this.detail.set(d); this.loading.set(false); },
      error: () => this.loading.set(false)
    });
  }

  submitOverride(decision: string): void {
    const id = this.detail()?.recommendation.id;
    if (!id) return;
    this.api.overrideRecommendation(id, { decision, reason: this.overrideReason.trim() }).subscribe({
      next: () => { this.overrideReason = ''; this.load(id); }
    });
  }

  submitFeedback(): void {
    const id = this.detail()?.recommendation.id;
    if (!id) return;
    this.api.submitFeedback(id, { outcome: this.feedbackOutcome.trim() }).subscribe({
      next: () => { this.feedbackOutcome = ''; this.load(id); }
    });
  }

  addConversation(): void {
    const rec = this.detail()?.recommendation;
    if (!rec?.entityId) return;
    this.api.createConversation({
      entityType: rec.entityType,
      entityId: rec.entityId,
      role: 'user',
      content: this.conversationText.trim()
    }).subscribe({
      next: () => { this.conversationText = ''; this.load(rec.id); }
    });
  }

  runForecast(): void {
    this.api.forecast({
      forecastType: 'sales',
      linkRecommendationId: this.detail()?.recommendation.id
    }).subscribe({
      next: r => this.forecastOutput.set(r.outputJson)
    });
  }

  t(key: string): string { return this.localization.translate(key); }
}
